import { AiDocumentField, AiDocumentTemplateOption, SectorAiDocumentConfig } from '../services/SectorDocumentService';

/** Filled by the system when sending — not collected from the customer. */
export const SYSTEM_AUTO_FIELD_KEYS = new Set([
  'numero_cotacao',
  'data_cotacao',
  'valor_total',
]);

const FIELD_COLLECT_HINTS: Record<string, string> = {
  cliente: 'nome do cliente',
  carro: 'categoria do veículo (Sedan, SUV, Minivan, Elétrico, etc.)',
  entrega: 'data (DD/MM/AAAA), horário (ex.: 10:00 AM) e local de retirada',
  devolucao: 'data (DD/MM/AAAA), horário e local de devolução',
  diarias: 'número de diárias',
  valor_diaria: 'tarifa em USD ($) — derive de cidade + categoria; não peça valor ao cliente',
  valor_taxa: 'taxa',
};

export const FALLBACK_DOCUMENT_FIELDS: AiDocumentField[] = [
  { key: 'numero_cotacao', label: 'Quote #', required: true },
  { key: 'data_cotacao', label: 'Date', required: true },
  { key: 'cliente', label: 'Customer', required: true },
  { key: 'entrega', label: 'Pickup', required: true },
  { key: 'devolucao', label: 'Drop-off', required: true },
  { key: 'carro', label: 'Vehicle', required: true },
  { key: 'diarias', label: 'Days', required: true },
  { key: 'valor_diaria', label: 'Daily rate', required: true },
  { key: 'valor_taxa', label: 'Fee/tax', required: false },
  { key: 'valor_total', label: 'Total', required: true },
];

const LEGACY_DOCUMENT_PROMPT_MARKERS = [
  /\[SEND_AI_DOCUMENT[^\]]*\]/i,
  /\bmarcador\b/i,
  /\bmarker\b/i,
];

const GENERIC_SHORT_DEFAULT = `Smart quote intake — you decide from the conversation when to offer, collect, or stop:
1. Judge customer intent from context before offering or collecting fields.
2. If they disengage — do not insist; answer their actual question.
3. Confirm details before sending the PDF.`;

function fieldHint(field: AiDocumentField): string {
  return FIELD_COLLECT_HINTS[field.key] ?? field.label;
}

function collectableFields(fields: AiDocumentField[]) {
  const schema = fields.length > 0 ? fields : FALLBACK_DOCUMENT_FIELDS;
  const required = schema.filter(f => f.required && !SYSTEM_AUTO_FIELD_KEYS.has(f.key));
  const optional = schema.filter(f => !f.required && !SYSTEM_AUTO_FIELD_KEYS.has(f.key));
  return { required, optional };
}

export const DOCUMENT_QUOTE_ENGAGEMENT = `PDF QUOTE module has highest priority when enabled: if the customer wants a quote, collect the template fields listed in the document prompt — do not substitute service menus or knowledge-base questionnaires from the agent. You decide engagement from conversation context. Reuse collected fields from QUOTE DRAFT.`;

export const DOCUMENT_CHAT_INTEGRATION = `Continue your normal chat flow (receptive, campaign follow-up, or active outbound). ${DOCUMENT_QUOTE_ENGAGEMENT}`;

export interface DocumentTemplateContext {
  templateId?: number | null;
  templateName?: string | null;
  layoutId?: string | null;
}

function inferTemplateDomain(
  fields: AiDocumentField[],
  layoutId?: string | null
): 'vehicle_rental' | 'generic' {
  const lid = (layoutId ?? '').toLowerCase();
  if (lid.includes('jmc') || lid.includes('rent') || lid.includes('car')) return 'vehicle_rental';
  const keys = new Set(fields.map(f => f.key.toLowerCase()));
  if (keys.has('carro') && (keys.has('entrega') || keys.has('diarias'))) return 'vehicle_rental';
  return 'generic';
}

function buildDomainBlock(domain: 'vehicle_rental' | 'generic', templateName?: string | null): string {
  const title = templateName?.trim()
    ? `PDF de cotação — ${templateName.trim()}`
    : 'PDF de cotação';
  if (domain === 'vehicle_rental') {
    return `${title}\nEste documento é uma cotação de ALUGUEL DE VEÍCULOS.\nCampo carro = categoria de veículo (Sedan, SUV, Minivan, etc.) — nunca categoria de serviço de TI ou outro catálogo.\nValores sempre em USD ($) — nunca R$ ou BRL.`;
  }
  return `${title}\nColete exatamente os campos do template configurado para este setor.`;
}

export function buildDocumentPromptContext(
  doc: Pick<SectorAiDocumentConfig, 'templateId' | 'templateName' | 'fields'> | null,
  templates: AiDocumentTemplateOption[]
): DocumentTemplateContext & { fields: AiDocumentField[] } {
  const fields = doc?.fields ?? [];
  const layoutId = templates.find(t => t.id === doc?.templateId)?.layoutId ?? null;
  return {
    templateId: doc?.templateId,
    templateName: doc?.templateName,
    layoutId,
    fields,
  };
}

export function buildDefaultDocumentPrompt(
  fields: AiDocumentField[],
  templateContext?: DocumentTemplateContext | null
): string {
  const schema = fields.length > 0 ? fields : FALLBACK_DOCUMENT_FIELDS;
  const domain = inferTemplateDomain(schema, templateContext?.layoutId);
  const { required, optional } = collectableFields(schema);

  let body = buildDomainBlock(domain, templateContext?.templateName) + '\n\n';
  body += 'Quando o cliente pedir cotação, orçamento ou PDF, colete os campos abaixo (um ou dois por mensagem):\n';
  body += required.map(f => `- ${f.key}: ${fieldHint(f)}`).join('\n') || '- (campos do template)\n';
  if (optional.length > 0) {
    body += '\n\nOpcional (pergunte ao cliente se necessário):\n';
    body += optional.map(f => `- ${f.key}: ${fieldHint(f)}`).join('\n');
  }
  body += '\n\nREGRAS:\n';
  body += '- Ofereça o PDF de forma leve; pare se o cliente mudar de assunto ou recusar.\n';
  body += '- Não re-pergunte campos já informados na conversa ou no rascunho.\n';
  body += '- Não substitua estes campos por menus de outro negócio (ex.: serviços de TI).\n';
  if (domain === 'vehicle_rental') {
    body += '- MOEDA: todos os valores em dólares (USD, formato $ 55,00) — nunca R$, BRL ou reais.\n';
    body += '- valor_diaria: informe o valor do catálogo no chat e confirme com o cliente antes do PDF.\n';
    body += '- entrega/devolução: data DD/MM/AAAA, horário e local — pergunte o que faltar; não assuma horários.\n';
  }
  body += '- Confirme os dados antes de enviar o PDF.\n';
  body += '- Ao enviar, use o marcador [SEND_AI_DOCUMENT|{json}] na mesma mensagem; nunca diga que enviou o PDF sem o marcador.\n';
  body += 'Número da cotação, data e total são gerados automaticamente pelo sistema.';
  return body;
}

export const DEFAULT_DOCUMENT_PROMPT = buildDefaultDocumentPrompt(FALLBACK_DOCUMENT_FIELDS);

export function isLegacyOrGenericDocumentPrompt(prompt: string | null | undefined): boolean {
  const trimmed = (prompt ?? '').trim();
  if (!trimmed) return true;
  if (/quando o cliente pedir cotação/i.test(trimmed)) return true;
  if (/when the customer asks for a quote/i.test(trimmed)) return true;
  if (/proactively offer and prepare a pdf quote/i.test(trimmed)) return true;
  if (/smart quote intake/i.test(trimmed)) return true;
  if (/continue your normal chat flow/i.test(trimmed)) return true;
  if (/pdf quote module has highest priority/i.test(trimmed)) return true;
  if (trimmed === GENERIC_SHORT_DEFAULT) return true;
  if (trimmed === DEFAULT_DOCUMENT_PROMPT) return false;
  return false;
}

/** User-editable prompt only — technical send markers are appended server-side for the AI. */
export function sanitizeDocumentPromptForDisplay(prompt: string | null | undefined): string {
  const trimmed = (prompt ?? '').trim();
  if (!trimmed) return DEFAULT_DOCUMENT_PROMPT;

  const cleaned = trimmed
    .split('\n')
    .filter(line => !LEGACY_DOCUMENT_PROMPT_MARKERS.some(re => re.test(line)))
    .join('\n')
    .trim();

  if (!cleaned) return DEFAULT_DOCUMENT_PROMPT;
  if (/quando o cliente pedir cotação/i.test(cleaned)) return DEFAULT_DOCUMENT_PROMPT;
  return cleaned;
}

export function resolveDocumentPrompt(
  storedPrompt: string | null | undefined,
  fields: AiDocumentField[],
  templateContext?: DocumentTemplateContext | null
): string {
  if (isLegacyOrGenericDocumentPrompt(storedPrompt)) {
    return buildDefaultDocumentPrompt(fields, templateContext);
  }
  return sanitizeDocumentPromptForDisplay(storedPrompt);
}

/** Regenerate prompt from active template schema (e.g. after template change). */
export function suggestDocumentPromptFromTemplate(
  fields: AiDocumentField[],
  templateContext?: DocumentTemplateContext | null
): string {
  return buildDefaultDocumentPrompt(fields, templateContext);
}

const RENTAL_RECEPTIVE_AGENT_PROMPT = `Você é o assistente receptivo de uma locadora de veículos no WhatsApp.
Seja amigável, direto e natural. Respostas curtas (1–3 frases).

IDENTIDADE
Você representa a locadora. Responda na língua do cliente (padrão: português brasileiro).

O QUE VOCÊ FAZ
- Saudações, dúvidas gerais sobre aluguel (horário, formas de pagamento, documentos, área de atendimento)
- Quando o cliente quiser cotação, orçamento ou PDF: siga as instruções do MÓDULO PDF ativo — colete os campos do template (cliente, carro, entrega, devolução, diárias, valor_diaria)
- Ofereça o PDF de forma leve quando fizer sentido; não insista se o cliente mudar de assunto

O QUE NÃO FAZ
- Não invente outro formulário de cotação fora do template PDF
- Não pergunte sobre serviços de TI, planos de suporte ou menus que não sejam do negócio de aluguel
- Não re-pergunte dados que o cliente já enviou na conversa

TRANSFERIR PARA HUMANO
Cliente frustrado, reclamação, pedido explícito de atendente, ou negociação/contrato complexo.

OBJETIVO
Ajudar com clareza e, quando for cotação em PDF, conduzir a coleta dos campos do template até confirmar e enviar o documento.`;

const RENTAL_RECEPTIVE_AGENT_KB = `LOCADORA — BASE DE CONHECIMENTO (CHAT AO VIVO)

SERVIÇOS
Aluguel de carros para turismo e negócios. Retirada e devolução em aeroporto ou endereço combinado.

PERGUNTAS FREQUENTES
P: Quais documentos preciso?
R: CNH válida, cartão de crédito em nome do condutor e documento de identidade.

P: Aceitam cartão?
R: Sim, cartão de crédito internacional na retirada para caução.

P: Tem SUV / carro automático?
R: Sim, conforme disponibilidade — informe modelo preferido na cotação.

COTAÇÃO EM PDF
Quando o cliente pedir cotação, colete pelo template PDF configurado no setor. Confirme antes de enviar.`;

function buildGenericReceptiveAgentPrompt(templateName?: string | null): string {
  const label = templateName?.trim() ? ` (${templateName.trim()})` : '';
  return `Você é o assistente receptivo no WhatsApp deste setor.
Seja claro, cordial e conciso (1–3 frases).

O QUE VOCÊ FAZ
- Responde dúvidas gerais do negócio usando a base de conhecimento
- Quando o cliente pedir cotação, orçamento ou PDF: siga o MÓDULO PDF ativo${label} — colete apenas os campos do template, não invente outro formulário

O QUE NÃO FAZ
- Não duplica a lista de campos do PDF no prompt do agente (isso fica no document prompt)
- Não substitui campos do template por menus de outro negócio ou scripts incompatíveis
- Não re-pergunte dados já informados na conversa

TRANSFERIR PARA HUMANO
Reclamação, pedido explícito de atendente ou negociação complexa.`;
}

/** Suggest receptive agent prompt aligned with active PDF template (no field list duplication). */
export function suggestAgentPromptFromDocumentTemplate(
  fields: AiDocumentField[],
  templateContext?: DocumentTemplateContext | null
): string {
  const domain = inferTemplateDomain(fields, templateContext?.layoutId);
  if (domain === 'vehicle_rental') return RENTAL_RECEPTIVE_AGENT_PROMPT;
  return buildGenericReceptiveAgentPrompt(templateContext?.templateName);
}

export function suggestAgentKnowledgeFromDocumentTemplate(
  fields: AiDocumentField[],
  templateContext?: DocumentTemplateContext | null
): string {
  const domain = inferTemplateDomain(fields, templateContext?.layoutId);
  if (domain === 'vehicle_rental') return RENTAL_RECEPTIVE_AGENT_KB;
  const label = templateContext?.templateName?.trim() ? ` — ${templateContext.templateName.trim()}` : '';
  return `BASE DE CONHECIMENTO — CHAT AO VIVO${label}

Use para FAQ geral do negócio. Cotação em PDF: campos e regras de coleta ficam no document prompt do setor — não repita a lista aqui.`;
}

export function isPdfQuoteModuleActive(doc: SectorAiDocumentConfig | null): boolean {
  return Boolean(doc?.enabled && doc.status === 'ready' && (doc.fields?.length ?? 0) > 0);
}

export function fieldDefaultsFromDocument(doc: SectorAiDocumentConfig | null): Record<string, string> {
  const out: Record<string, string> = {};
  (doc?.fields ?? []).forEach(f => {
    out[f.key] = f.sampleValue ?? '';
  });
  return out;
}

export function mergePreviewFields(
  fields: AiDocumentField[],
  mock: Record<string, string>
): Record<string, string> {
  const out: Record<string, string> = { ...mock };
  fields.forEach(f => {
    if (!out[f.key] && f.sampleValue) out[f.key] = f.sampleValue;
  });
  return out;
}

export function isDocumentReady(doc: SectorAiDocumentConfig | null): boolean {
  return doc?.status === 'ready';
}

export function canShowDocumentsTab(hasModule: boolean): boolean {
  return hasModule;
}

/** Document auto-send is on when the sector document config is enabled (works with receptive or outbound AI). */
export function canEnableDocumentAutoSend(_receptiveEnabled?: boolean): boolean {
  return true;
}

export function isInboundDocumentAgent(agent: {
  isEnabled: boolean;
  capReceptive: boolean;
  capCampaign: boolean;
  capFollowup: boolean;
  capCrm: boolean;
}): boolean {
  return agent.isEnabled
    && (agent.capReceptive || agent.capCampaign || agent.capFollowup || agent.capCrm);
}

export function agentCapabilitySummary(agent: {
  capReceptive: boolean;
  capCampaign: boolean;
  capFollowup: boolean;
  capCrm: boolean;
}): string {
  const caps: string[] = [];
  if (agent.capReceptive) caps.push('Receptive');
  if (agent.capCampaign) caps.push('Campaign');
  if (agent.capFollowup) caps.push('Follow-up');
  if (agent.capCrm) caps.push('CRM');
  return caps.join(' · ');
}

export type DocumentLinkCapability = 'receptive' | 'campaign' | 'followup' | 'crm';

export const DOCUMENT_LINK_CAPABILITIES: { key: DocumentLinkCapability; label: string; capKey: keyof Pick<UnifiedAiAgentCapabilityShape, 'capReceptive' | 'capCampaign' | 'capFollowup' | 'capCrm'> }[] = [
  { key: 'receptive', label: 'Receptive chat', capKey: 'capReceptive' },
  { key: 'campaign', label: 'Campaign replies', capKey: 'capCampaign' },
  { key: 'followup', label: 'Follow-up', capKey: 'capFollowup' },
  { key: 'crm', label: 'CRM automation', capKey: 'capCrm' },
];

type UnifiedAiAgentCapabilityShape = {
  capReceptive: boolean;
  capCampaign: boolean;
  capFollowup: boolean;
  capCrm: boolean;
};

export type LinkedAgentsByCapability = Record<DocumentLinkCapability, number | ''>;

export const EMPTY_LINKED_AGENTS: LinkedAgentsByCapability = {
  receptive: '',
  campaign: '',
  followup: '',
  crm: '',
};

export interface AiDocumentLinkedAgents {
  receptive?: number | null;
  campaign?: number | null;
  followup?: number | null;
  crm?: number | null;
}

export function parseLinkedAgents(
  linkedAgents?: AiDocumentLinkedAgents | null,
  legacyLinkedAgentId?: number | null
): LinkedAgentsByCapability {
  const out: LinkedAgentsByCapability = { ...EMPTY_LINKED_AGENTS };
  if (linkedAgents) {
    for (const cap of DOCUMENT_LINK_CAPABILITIES) {
      const val = linkedAgents[cap.key];
      if (typeof val === 'number' && val > 0) out[cap.key] = val;
    }
  }
  if (out.receptive === '' && typeof legacyLinkedAgentId === 'number' && legacyLinkedAgentId > 0) {
    out.receptive = legacyLinkedAgentId;
  }
  return out;
}

export function serializeLinkedAgents(links: LinkedAgentsByCapability): AiDocumentLinkedAgents {
  return {
    receptive: links.receptive === '' ? null : links.receptive,
    campaign: links.campaign === '' ? null : links.campaign,
    followup: links.followup === '' ? null : links.followup,
    crm: links.crm === '' ? null : links.crm,
  };
}

export function agentsForDocumentCapability<T extends UnifiedAiAgentCapabilityShape & { id: number; name: string; isEnabled: boolean }>(
  agents: T[],
  capability: DocumentLinkCapability
): T[] {
  const cap = DOCUMENT_LINK_CAPABILITIES.find(c => c.key === capability);
  if (!cap) return [];
  return agents.filter(a => a.isEnabled && a[cap.capKey]);
}

export function fieldBadgeKind(field: AiDocumentField): 'auto' | 'required' | 'optional' {
  if (SYSTEM_AUTO_FIELD_KEYS.has(field.key)) return 'auto';
  return field.required ? 'required' : 'optional';
}

/** Ensures the sector's active template appears in the picker even if the catalog list lags. */
export function mergeTemplateOptions(
  assigned: AiDocumentTemplateOption[],
  doc: Pick<SectorAiDocumentConfig, 'templateId' | 'templateName'> | null
): AiDocumentTemplateOption[] {
  const tid = doc?.templateId;
  if (tid == null || tid <= 0) return assigned;
  if (assigned.some(t => t.id === tid)) return assigned;
  return [
    ...assigned,
    {
      id: tid,
      name: doc?.templateName?.trim() || `Template #${tid}`,
      layoutId: '',
      status: 'ready',
    },
  ];
}
